import { Formik, useFormikContext } from "formik";
import React, {FC, useCallback, useEffect, useState} from "react";
import GridLayout from "react-grid-layout";
import { useHistory } from "react-router-dom";
import { useGlobalState } from "../../Providers";
import { toast } from "react-toastify";
import ActivityFrame from "../../components/activity-frame/activity-frame";
import Breadcrumb from "../../components/breadcrumb/breadcrumb";
import CommandWidget from "../../components/dashboard/command/command-widget";
import "../../components/dashboard/style.css";
import { DialogContent } from "../../components/dialog/Dialog";
import { useDialog } from "../../components/dialog/DialogContext";
import Togglebox from "../../components/togglebox/togglebox";
import { WIDGET_TYPES, X_POSITION, Y_POSITION } from "../../constants/widget-constants";
import Grid from "../../images/8.jpg";
import { ButtonTypes } from "../../models/ButtonTypes";
import { SettingData } from "../../models/SettingData";
import { Widget } from "../../models/Widget";
import { saveSettingData } from "../../services/settingDataService";
import { showError } from "../../services/utilities/ServiceError";
import { deleteWidget, getWidgets } from "../../services/widgetService";
import { TranslationKeys } from "../TranslationKeys";
import { useLocale } from "../localeContext";
import { DashboardContext } from "./DashboardContext";
import ShortcutWidget from "../../components/dashboard/shortcut/shortcut-widget";
import LiveTransactionWidget from "../../components/dashboard/live-transactions/live-transaction-widget";
import QuickEnrolWidget from "../../components/dashboard/quick-enrol/quick-enrol-widget";
import { useSelector } from "react-redux";
import { IAppState } from "../../store/types";

interface DashboardContextProviderProps {
    breadcrumb: any[];
}

export const DashboardContextProvider: FC<DashboardContextProviderProps> = ({ breadcrumb }) => {
    return (
        <Formik initialValues={{ mode: false }} onSubmit={() => { }}>
            <DashboardContextProviderInner breadcrumb={breadcrumb} />
        </Formik>
    );
};

const DashboardContextProviderInner: FC<DashboardContextProviderProps> = ({ breadcrumb }) => {
    // TODO: We need to be on edit mode after adding a new widget

    const { translate } = useLocale();
    const history = useHistory();
    const [editMode, setEditMode] = useState<boolean>(false);
    const { setFieldValue, values } = useFormikContext<any>();
    const [layout, setLayout] = useState<any>([]);
    const [layoutContent, setLayoutContent] = useState<any[]>([]);
    const [busy, setBusy] = useState<boolean>(false);
    const dialog = useDialog();
    const [isDraggable, setIsDraggable] = useState<boolean>(false);
    const user = useSelector((state: IAppState) => (state.userInfo));

    //Widgets we get on initial load, they only need to be set once onLoad and not change
    const [originalWidgets, setOriginalWidgets] = useState<Widget[]>([]);

    // Updated widgets
    const [updatedWidgets, setUpdatedWidgets] = useState<any[]>([]);
    const [isInitialized, setIsInitialized] = useState(false);

    // globalContext.controlPanelButton for showing an active button option on the panel
    // @refreshDashboard can be used to refresh the dashboard after saving a widget
    const globalContext = useGlobalState();

    useEffect(() => {
        setFieldValue("mode", editMode);
    }, [])

    useEffect(() => {
        if (globalContext?.refreshDashboard) {
            onReload(false);
            globalContext?.setRefreshDashboard(false);

            // Maintain edit mode state after refresh
            setEditMode(editMode);
            setFieldValue("mode", editMode);
            setIsDraggable(editMode);
        }
    }, [globalContext?.refreshDashboard]);

    const onReload = useCallback(async (discardChanges: boolean = false) => {
        try {
            setBusy(true);
            const widgets = await getWidgets(translate, user.id, user.permissions, { pageSize: 100 });
            const newLayout = widgets.map((widget) => ({
                i: widget.id.toString(),
                x: widget.x,
                y: widget.y,
                w: widget.width,
                h: widget.height
            }));

            // Only merge with local changes if we're not discarding changes
            const finalLayout = discardChanges ? newLayout : newLayout.map(item => {
                const updatedItem = updatedWidgets.find(w => w.i === item.i);
                return updatedItem || item;
            });

            setLayout(finalLayout);
            setLayoutContent(widgets.map(getWidgetComponetByType));
            setOriginalWidgets(widgets);

            // Reset updatedWidgets if we're discarding changes
            if (discardChanges) {
                setUpdatedWidgets([]);
            }
        } catch (error) {
            showError(error, toast.warn);
        } finally {
            setBusy(false);
        }
    }, [translate, user.id, user.permissions]);

    useEffect(() => {
        if (!isInitialized) {
            onReload(false);
            setFieldValue("mode", false);
            setIsInitialized(true);
        } else if (globalContext?.refreshDashboard) {
            onReload(false);
            globalContext.setRefreshDashboard(false);
        }
    }, [isInitialized, globalContext?.refreshDashboard, onReload, setFieldValue]);

    const onEditReload = (widget: Widget) => {
        const newLayoutContent = layoutContent.filter((item) => item.key !== widget.id.toString());
        newLayoutContent.push(getWidgetComponetByType(widget));
        setLayoutContent(newLayoutContent);
    }

    const getWidgetComponetByType = (widget: Widget) => {
        switch (widget.type) {
            case WIDGET_TYPES.COMMANDS_WIDGET:
                return {
                    key: widget.id.toString(),
                    content: <CommandWidget widget={widget} />
                }
            case WIDGET_TYPES.SHORTCUT_WIDGET:
                return {
                    key: widget.id.toString(),
                    content: <ShortcutWidget widget={widget} />
                }
            case WIDGET_TYPES.LIVE_TRANSACTIONS_WIDGET:
                return {
                    key: widget.id.toString(),
                    content: <LiveTransactionWidget widget={widget} />
                }
            case WIDGET_TYPES.QUICK_ENROLL_WIDGET:
                return {
                    key: widget.id.toString(),
                    content: <QuickEnrolWidget
                        widget={widget} />
                }

            default:
                return null;
        }
    }

    const handleDelete = (id: string) => {
        const dialogContent: DialogContent = {
            title: translate.byKey(TranslationKeys.deleteRecord),
            message: translate.byKey(TranslationKeys.permanentlyDeleteRecord),
            buttons: [
                {
                    id: "btn_confirmDelete",
                    onClick: () => { onDelete(id) },
                    text: translate.byKey(TranslationKeys.confirm),
                    type: ButtonTypes.RedRoundedButton
                },
                {
                    id: "btn_Cancel",
                    onClick: () => { }, // Empty onClick will just close the dialog
                    text: translate.byKey(TranslationKeys.cancel),
                    type: ButtonTypes.TextButton,
                    className: "justify-center px-4 py-1 text-base font-medium sm:ml-3 sm:w-auto sm:text-sm"
                }
            ]
        }

        dialog.pushInfo(dialogContent);
    }

    const handleMoveWidget = () => {
        const dialogContent: DialogContent = {
            title: translate.byKey(TranslationKeys.changeDashboardLayout),
            message: translate.byKey(TranslationKeys.widgetMoveConfirmation),
            buttons: [
                {
                    id: "btn_confirmMoveWidget",
                    onClick: () => { updateWidgetPosition() },
                    text: translate.byKey(TranslationKeys.confirm),
                    type: ButtonTypes.GreenRoundedButton
                },
                {
                    id: "btn_DontSave",
                    onClick: () => { onReload(true) },
                    text: translate.byKey(TranslationKeys.dontSave),
                    type: ButtonTypes.RedRoundedButton,
                },
                {
                    id: "btn_Cancel",
                    onClick: () => { setEditMode(true); setFieldValue("mode", true); setIsDraggable(true) },
                    text: translate.byKey(TranslationKeys.cancel),
                    type: ButtonTypes.TextButton,
                    className: "justify-center px-4 py-1 text-base font-medium sm:ml-3 sm:w-auto sm:text-sm"
                }
            ]
        }

        dialog.pushInfo(dialogContent);
    }

    const onDelete = (key: string) => {
        deleteWidget(key).then((response) => {
            onReload(false);
            toast.success(translate.byKey(TranslationKeys.deleteSuccess));
        }).catch((error) => {
            showError(error, toast.warn);
        })
    }

    const onLayoutChange = (newLayout) => {
        setUpdatedWidgets(newLayout);
        setLayout(newLayout);
    };

    const getContent = () => {
        return layoutContent.map((item) => {
            return <div key={item.key}>
                {item.content}
            </div>
        })
    }

    const onEditModeChange = () => {
        setFieldValue("mode", !values.mode);
        setEditMode(!values.mode);
        setIsDraggable(!values.mode);

        if (values.mode) {
            const settingDataToEdit: SettingData[] = buildUpdatedWidgetSettingData();
            if (settingDataToEdit.length > 0) {
                handleMoveWidget();
            }
        }
    }

    const updateWidgetPosition = async () => {
        try {
            setBusy(true);
            const settingDataToEdit = buildUpdatedWidgetSettingData();

            await Promise.all(settingDataToEdit.map(saveSettingData));

            toast.success(translate.byKey(TranslationKeys.success));
            await onReload(false)
        } catch (error) {
            showError(error, toast.warn);
        } finally {
            setBusy(false);
        }
    };

    const buildUpdatedWidgetSettingData = (): SettingData[] => {
        const settingDataToEdit: SettingData[] = [];

        updatedWidgets.forEach((updatedWidget) => {
            const originalWidget = originalWidgets.find((item) => item.id.toString() === updatedWidget.i);

            // ignore if the orginal widget is not found
            if (!originalWidget) {
                return;
            }

            appendSettingToEdit(originalWidget.x, updatedWidget.x, X_POSITION, originalWidget.settingDatasRefs, settingDataToEdit);
            appendSettingToEdit(originalWidget.y, updatedWidget.y, Y_POSITION, originalWidget.settingDatasRefs, settingDataToEdit);
        });

        return settingDataToEdit;
    }

    const appendSettingToEdit = (original: number, current: number, positionESysname: string, settingDatasRefs: any, settingDataToEdit: SettingData[]) => {
        if (current !== original) {
            const xCoordiantesSettingDataId = settingDatasRefs?.find((item) => item.eSysname === positionESysname)?.id;

            if (xCoordiantesSettingDataId) {
                settingDataToEdit.push({
                    id: xCoordiantesSettingDataId,
                    sdValstring: current.toString()
                });
            }
        }
    }

    return <DashboardContext.Provider value={{ editMode, onDelete: handleDelete, widgets: originalWidgets, onReload, setIsDraggable, onEditReload }}>
        <div id="dashboardContainer" className="pt-4 pl-4 pr-3 flex flex-col flex-grow h-full">
            <Breadcrumb
                path={breadcrumb}
                onClick={(item) => history.push(item.value)} />

            <ActivityFrame busy={busy} className='flex flex-col h-full flex-grow bg-white'>
                <div className={`bg-white  h-full border-gray-bar border-solid border-b flex flex-col flex-grow`}>
                    <div className={`flex flex-row bg-gray-bar px-2 pt-2 pb-2 justify-start items-center`} style={{ height: '40px' }}>
                        <span className="text-sm pl-4 pt-1" >{translate.byKey(TranslationKeys.editor)}</span>
                        <div className="my-0 pl-2" id="dashboardEditModeTogglebox">
                            <Togglebox name="mode" label="" onChanged={onEditModeChange} />
                        </div>
                    </div>
                    <div
                        className={`flex bg-white  w-full h-full overflow-y-auto`}
                        id="dashboardWidgets"
                        style={editMode ? {
                            backgroundImage: `url(${Grid})`,
                            backgroundPosition: "center",
                            backgroundRepeat: " no-repeat",
                            backgroundSize: "cover"
                        } : {}}>
                        <GridLayout
                            className="layout"
                            cols={20}
                            layout={layout}
                            rowHeight={8}
                            width={1580}
                            onLayoutChange={(e) => onLayoutChange(e)}
                            isDraggable={isDraggable}
                            isResizable={false}
                            isBounded={true}>
                            {getContent()}
                        </GridLayout>
                    </div>
                </div>
            </ActivityFrame>
        </div>
    </DashboardContext.Provider>
}
